import type { DSLFile, FormComponent, FormDSL } from '@commenda/types';
import { flattenComponents } from './dsl-traversal';
import { interpolateFieldTokens } from './field-tokens';

type FormDSLLike = FormDSL | DSLFile;

const componentsByFieldId = (
  dsl?: FormDSLLike | null
): Record<string, FormComponent> => {
  if (!dsl) return {};
  const map: Record<string, FormComponent> = {};
  flattenComponents(dsl).forEach((component) => {
    if (component.fieldId) map[component.fieldId] = component;
  });
  return map;
};

const formatValue = (value: unknown, component?: FormComponent): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value
      .map((item) => formatValue(item, component))
      .filter((text) => text.length > 0)
      .join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number')
    return Number.isFinite(value) ? `${value}` : '';
  if (typeof value !== 'string') return '';

  const text = value.trim();
  if (!text) return '';

  const option = component?.options?.find((o) => `${o.value}` === text);
  return option?.label ?? text;
};

export function getEntryTitleTemplate(
  override: unknown,
  dsl?: FormDSLLike | null
): string | undefined {
  const overrideTemplate =
    typeof override === 'string' ? override.trim() : undefined;
  if (overrideTemplate) return overrideTemplate;

  const formTemplate = dsl?.form?.settings?.display?.entryTitleTemplate?.trim();
  return formTemplate || undefined;
}

export function previewEntryTitleTemplate(
  template: string,
  fields: { fieldId: string; label: string }[]
): string {
  const labels = new Map(fields.map(({ fieldId, label }) => [fieldId, label]));
  return interpolateFieldTokens(
    template,
    (fieldId) => labels.get(fieldId) ?? fieldId
  ).text;
}

export function resolveEntryTitle({
  entry,
  template,
  dsl,
}: {
  entry: Record<string, unknown> | undefined;
  template: string | undefined;
  dsl?: FormDSLLike | null;
}): string | null {
  if (!entry || !template) return null;

  const components = componentsByFieldId(dsl);
  const { text, resolvedCount } = interpolateFieldTokens(template, (fieldId) =>
    formatValue(entry[fieldId], components[fieldId])
  );

  return resolvedCount > 0 && text ? text : null;
}
